import type { ReactNode } from 'react';
import { Aviso, Botao } from './forma.tsx';
import './painel.css';

/** Formulário à esquerda, lista à direita. É o arranjo que a recepção já
 *  conhece do sistema atual: quem cadastra é quem consulta, no mesmo
 *  atendimento, e clicar numa linha traz o registro para edição. */
export function Bancada({ children }: { children: ReactNode }) {
  return <div className="bancada">{children}</div>;
}

export function Painel({
  titulo,
  editando,
  erro,
  salvo,
  salvando,
  rotuloCriar,
  aoEnviar,
  aoCancelar,
  aoExcluir,
  excluindo,
  somenteLeitura,
  children,
}: {
  titulo: string;
  editando: boolean;
  erro?: string;
  salvo?: string;
  salvando?: boolean;
  rotuloCriar: string;
  aoEnviar: (e: React.FormEvent) => void;
  aoCancelar: () => void;
  aoExcluir?: () => void;
  excluindo?: boolean;
  somenteLeitura?: boolean;
  children: ReactNode;
}) {
  return (
    <section className="painel cartao" aria-label={titulo}>
      <header className="painel__topo">
        <h2 className="painel__titulo">{titulo}</h2>
      </header>

      <form className="painel__campos grade" onSubmit={aoEnviar} noValidate>
        {erro && (
          <div className="campo campo--total">
            <Aviso>{erro}</Aviso>
          </div>
        )}
        {salvo && !editando && (
          <div className="campo campo--total">
            <Aviso tipo="sucesso">{salvo}</Aviso>
          </div>
        )}

        {children}

        {!somenteLeitura && (
          <div className="painel__acoes campo--total">
            <Botao type="submit" variante="primario" carregando={salvando}>
              {salvando ? 'Salvando…' : editando ? 'Salvar alterações' : rotuloCriar}
            </Botao>
            {editando && (
              <>
                <Botao type="button" variante="silencioso" onClick={aoCancelar}>
                  Cancelar
                </Botao>
                {aoExcluir && (
                  <Botao
                    type="button"
                    variante="perigo"
                    className="painel__excluir"
                    carregando={excluindo}
                    onClick={aoExcluir}
                  >
                    Excluir
                  </Botao>
                )}
              </>
            )}
          </div>
        )}
      </form>
    </section>
  );
}

/** Caixa da lista, com os três estados que ela pode ter. */
export function Lista({
  carregando,
  erro,
  vazio,
  recarregando,
  children,
}: {
  carregando: boolean;
  erro?: string;
  vazio?: { titulo: string; apoio: string };
  recarregando?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="cartao lista-caixa" data-recarregando={recarregando || undefined}>
      {carregando ? (
        <p className="vazio">Carregando…</p>
      ) : erro ? (
        <div className="vazio">
          <p className="vazio__titulo">Não foi possível carregar</p>
          <p>{erro}</p>
        </div>
      ) : vazio ? (
        <div className="vazio">
          <p className="vazio__titulo">{vazio.titulo}</p>
          <p>{vazio.apoio}</p>
        </div>
      ) : (
        children
      )}
    </div>
  );
}

/** Atalho A–Z. Letra sem registro fica desabilitada: a lista não some sem
 *  motivo aparente quando alguém clica numa letra vazia. */
export function Iniciais({
  disponiveis,
  atual,
  aoEscolher,
}: {
  disponiveis: string[];
  atual: string;
  aoEscolher: (letra: string) => void;
}) {
  const LETRAS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
  return (
    <div className="iniciais" role="group" aria-label="Filtrar pela inicial">
      <button className="iniciais__letra" data-ativa={atual === '' || undefined} onClick={() => aoEscolher('')}>
        Todos
      </button>
      {LETRAS.map((l) => (
        <button
          key={l}
          className="iniciais__letra"
          disabled={!disponiveis.includes(l)}
          data-ativa={atual === l || undefined}
          onClick={() => aoEscolher(l === atual ? '' : l)}
        >
          {l}
        </button>
      ))}
    </div>
  );
}

export function Busca({
  valor,
  aoMudar,
  placeholder,
}: {
  valor: string;
  aoMudar: (v: string) => void;
  placeholder: string;
}) {
  return (
    <input
      className="entrada busca"
      type="search"
      value={valor}
      onChange={(e) => aoMudar(e.target.value)}
      placeholder={placeholder}
      aria-label={placeholder}
    />
  );
}

/** Interruptor. Usa checkbox de verdade por baixo — teclado e leitor de tela
 *  funcionam sem nada extra. */
export function Chave({
  rotulo,
  descricao,
  ...resto
}: { rotulo: string; descricao?: string } & React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <label className="chave">
      <input type="checkbox" {...resto} />
      <span className="chave__marca" aria-hidden="true" />
      <span className="chave__texto">
        {rotulo}
        {descricao && <span className="chave__descricao">{descricao}</span>}
      </span>
    </label>
  );
}
